import React, { useEffect, useState } from 'react'
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api'
import { mapStyle } from './mapStyle'
import { Store } from './StoreList'

interface StoreListProps {
    storeList: Store[]
    activeStore: number | null
    setActiveStore: (storeId: number | null) => void
    setExpandedStoreId: (storeId: number | null) => void
    filteredStores?: Store[]
    googleMapApi: string
    center: google.maps.LatLngLiteral
    setCenter: React.Dispatch<
        React.SetStateAction<google.maps.LatLngLiteral | null>
    >
    zoom: number
    setZoom: React.Dispatch<React.SetStateAction<number>>
    isUserGeolocation: boolean
    setIsUserGeolocation: React.Dispatch<React.SetStateAction<boolean>>
}

export const StoreLocatorMap: React.FC<StoreListProps> = ({
    storeList,
    activeStore,
    setActiveStore,
    setExpandedStoreId,
    filteredStores = [],
    googleMapApi,
    center,
    setCenter,
    zoom,
    setZoom,
    isUserGeolocation,
    setIsUserGeolocation
}) => {
    const [isLoaded, setIsLoaded] = useState(false)
    const items = storeList

    useEffect(() => {
        if (activeStore !== null && items) {
            const activeStoreItem = items.find(
                (store: Store) => store.storelocator_id === activeStore
            )
            if (activeStoreItem) {
                const { latitude, longitude } = activeStoreItem
                setCenter({
                    lat: parseFloat(latitude),
                    lng: parseFloat(longitude)
                })
                setZoom(15)
            }
        } else if (!isUserGeolocation) {
            fitBoundsToFilteredStores()
        }
    }, [activeStore, items, isUserGeolocation])

    const mapContainerStyle = {
        width: '100%',
        height: '100%'
    }

    const handleMarkerClick = (storeId: number) => {
        setActiveStore(storeId)
        setExpandedStoreId(storeId)
    }

    const fitBoundsToFilteredStores = () => {
        if (items.length > 0 && isLoaded) {
            const bounds = new window.google.maps.LatLngBounds()
            items.forEach((store) => {
                bounds.extend({
                    lat: parseFloat(store.latitude),
                    lng: parseFloat(store.longitude)
                })
            })
            setCenter({
                lat: bounds.getCenter().lat(),
                lng: bounds.getCenter().lng()
            })

            const numStores = filteredStores.length
            setZoom(numStores > 1 ? 11 : 12)
        }
    }

    const handleOnLoad = (map: google.maps.Map) => {
        const bounds = new google.maps.LatLngBounds()
        items?.forEach(({ latitude, longitude }) =>
            bounds.extend({
                lat: parseFloat(latitude),
                lng: parseFloat(longitude)
            })
        )
        map.fitBounds(bounds)
        setIsLoaded(true)
    }

    return (
        <LoadScript googleMapsApiKey={googleMapApi}>
            <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={zoom}
                onClick={() => {
                    setActiveStore(null)
                    setIsUserGeolocation(false)
                }}
                onLoad={handleOnLoad}
                options={{
                    styles: mapStyle
                }}
            >
                {storeList?.map((store: Store) => {
                    const position = {
                        lat: parseFloat(store.latitude),
                        lng: parseFloat(store.longitude)
                    }

                    return (
                        <Marker
                            key={store.storelocator_id}
                            position={position}
                            onClick={() =>
                                handleMarkerClick(store.storelocator_id)
                            }
                            options={{
                                icon: {
                                    path:
                                        'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9',
                                    fillColor:
                                        activeStore === store.storelocator_id
                                            ? '#6E0F9D'
                                            : '#000',
                                    fillOpacity: 1,
                                    strokeColor: '#fff',
                                    scale:
                                        activeStore === store.storelocator_id
                                            ? 2.5
                                            : 2
                                }
                            }}
                            zIndex={
                                activeStore === store.storelocator_id ? 100 : 1
                            }
                        />
                    )
                })}
            </GoogleMap>
        </LoadScript>
    )
}
